import { faX } from '@fortawesome/free-solid-svg-icons/faX';
import { ModalProps as AntdModalProps } from 'antd';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FontSize } from '../../theme/FontSize';
import { Spacing } from '../../theme/Spacing';
import { Modal as AntdModal } from '../../utils/local-antd';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { ParagraphProps, Text } from '../Text';
import { WarningModal } from '../WarningModal';

const StyledAntdModal = styled(AntdModal)`
  .ant-modal-close {
    display: none;
  }

  .ant-modal-content {
    background-color: ${({ theme }) => theme.bgCanvas};
  }
`;

const CloseButton = styled(Button)`
  margin: 0;
  padding: 0;
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.fgSecondary};
`;

const StyledTitle = styled(Text)`
  font-size: ${FontSize.l} !important;
  font-weight: bold;
  text-align: start !important;
  color: ${({ theme }) => theme.fgPrimary};
`;

const StyledHr = styled.hr`
  height: 1.5px;
  opacity: 1;
  background-color: ${({ theme }) => theme.fgPrimary};
  width: 100%;
  border: 0;
  margin-bottom: ${Spacing.m};
`;

const ModalTitleWrapper = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.bgCanvas};
`;

interface ModalProps extends AntdModalProps {
  children: React.ReactNode;
  title?: string;
  titleProps?: ParagraphProps;
  content?: React.CSSProperties;
  overlay?: React.CSSProperties;
  open: boolean;
  onClose?: VoidFunction;
  showCloseWarning?: boolean;
  closeWarningDescription?: string;
  onConfirmClose?: VoidFunction;
  className?: string;
  width?: number | React.CSSProperties['width'];
  style?: React.CSSProperties;
  destroyOnClose?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  onClose,
  children,
  content,
  overlay,
  title,
  titleProps = {},
  open,
  showCloseWarning = false,
  closeWarningDescription,
  onConfirmClose,
  className,
  style,
  width,
  destroyOnClose,
  ...props
}) => {
  const { t } = useTranslation();
  const [warningModalVisible, setWarningModalVisible] = useState(false);

  const onLocalClose = showCloseWarning
    ? () => setWarningModalVisible(true)
    : onClose;

  const onLocalConfirmClose = () => {
    setWarningModalVisible(false);
    onConfirmClose?.();
    onClose?.();
  };

  return (
    <Fragment>
      <StyledAntdModal
        open={open}
        onCancel={onClose}
        onClose={onLocalClose}
        footer={null}
        className={className}
        width={width}
        style={style}
        destroyOnClose={destroyOnClose}
        title={
          <ModalTitleWrapper>
            <StyledTitle {...titleProps}>{title}</StyledTitle>
            <CloseButton icon={faX} iconWidth="lg" onClick={onClose} />
          </ModalTitleWrapper>
        }
        {...props}
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <StyledHr className="ant-modal-hr" />

          {children}
        </Flex>
      </StyledAntdModal>

      {showCloseWarning ? (
        <WarningModal
          description={closeWarningDescription || t('warning.sure_to_leave')}
          open={warningModalVisible}
          onClose={() => setWarningModalVisible(false)}
          onConfirm={onLocalConfirmClose}
        />
      ) : null}
    </Fragment>
  );
};
